/**
 * Single slider control with mode toggle for year/zany detent selection
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import styles from './SweepControl.module.css';

interface DetentOption {
  id: string;
  label: string;
}

type Mode = 'years' | 'zany';

interface DetentControlProps {
  selectedDetentId: string;
  onDetentChange: (detentId: string) => void;
}

export const DetentControl: React.FC<DetentControlProps> = ({
  selectedDetentId,
  onDetentChange,
}) => {
  const { isDarkMode } = useTheme();
  const [years, setYears] = useState<DetentOption[]>([]);
  const [zanyOptions, setZanyOptions] = useState<DetentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<Mode>('years');
  const [lastYearIndex, setLastYearIndex] = useState(0);
  const [lastZanyIndex, setLastZanyIndex] = useState(0);

  // Fetch available detents on mount
  useEffect(() => {
    const fetchDetents = async () => {
      try {
        const response = await fetch('/data/detents.json');
        const data = await response.json();

        const yearOptions: DetentOption[] = [];
        const zanyDetents: DetentOption[] = [];

        // Add years
        if (data.years && Array.isArray(data.years)) {
          data.years.forEach((year: number) => {
            yearOptions.push({
              id: year.toString(),
              label: year.toString()
            });
          });
        }

        // Add zany detents
        if (data.zany && Array.isArray(data.zany)) {
          data.zany.forEach((zanyDetent: { id: string; label: string }) => {
            zanyDetents.push({
              id: zanyDetent.id,
              label: zanyDetent.label
            });
          });
        }

        setYears(yearOptions);
        setZanyOptions(zanyDetents);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch detent options:', err);
        setLoading(false);
      }
    };

    fetchDetents();
  }, []);

  // Sync mode with current selection
  useEffect(() => {
    const yearIndex = years.findIndex((y) => y.id === selectedDetentId);
    if (yearIndex !== -1) {
      setLastYearIndex(yearIndex);
      setMode('years');
      return;
    }

    const zanyIndex = zanyOptions.findIndex((z) => z.id === selectedDetentId);
    if (zanyIndex !== -1) {
      setLastZanyIndex(zanyIndex);
      setMode('zany');
    }
  }, [selectedDetentId, years, zanyOptions]);

  const options = mode === 'years' ? years : zanyOptions;
  const derivedIndex = options.findIndex((o) => o.id === selectedDetentId);
  const fallbackIndex = mode === 'years'
    ? Math.min(lastYearIndex, Math.max(0, options.length - 1))
    : Math.min(lastZanyIndex, Math.max(0, options.length - 1));
  const activeIndex = derivedIndex >= 0 ? derivedIndex : fallbackIndex;
  const currentOption = options[activeIndex];
  const hasOptions = options.length > 0;

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(event.target.value);
    if (index >= 0 && index < options.length) {
      if (mode === 'years') {
        setLastYearIndex(index);
      } else {
        setLastZanyIndex(index);
      }
      onDetentChange(options[index].id);
    }
  };

  const handleModeToggle = (newMode: Mode) => {
    if (newMode !== mode) {
      setMode(newMode);
      const newOptions = newMode === 'years' ? years : zanyOptions;
      if (newOptions.length > 0) {
        const storedIndex = newMode === 'years'
          ? Math.min(lastYearIndex, newOptions.length - 1)
          : Math.min(lastZanyIndex, newOptions.length - 1);
        const targetIndex = storedIndex >= 0 ? storedIndex : 0;
        const nextDetent = newOptions[targetIndex];
        if (nextDetent) {
          onDetentChange(nextDetent.id);
        }
      }
    }
  };

  if (loading || (years.length === 0 && zanyOptions.length === 0)) {
    return <div className={`${styles.container} ${isDarkMode ? styles.dark : styles.light}`}>Loading detents...</div>;
  }

  const position = options.length > 1
    ? activeIndex / (options.length - 1)
    : 0;
  const showProgress = options.length > 1;

  return (
    <div className={`${styles.container} ${isDarkMode ? styles.dark : styles.light}`}>
      {/* Mode Toggle */}
      <div className={styles.modeToggle}>
        <button
          className={`${styles.modeButton} ${mode === 'years' ? styles.modeActive : ''}`}
          onClick={() => handleModeToggle('years')}
        >
          Years
        </button>
        <button
          className={`${styles.modeButton} ${mode === 'zany' ? styles.modeActive : ''}`}
          onClick={() => handleModeToggle('zany')}
        >
          Zany
        </button>
      </div>

      {/* Single Slider */}
      <div className={styles.sliderSection}>
        <div className={styles.header}>
          <label className={styles.label}>
            ← {mode === 'years' ? 'Year' : 'Zany'} →
          </label>
          <span className={styles.detentName}>
            {currentOption?.label || 'None'}
          </span>
        </div>

        <div className={styles.sliderContainer}>
          <div className={styles.sliderTrack}>
            <input
              type="range"
              className={styles.slider}
              min="0"
              max={Math.max(options.length - 1, 0)}
              step="1"
              value={hasOptions ? activeIndex : 0}
              onChange={handleSliderChange}
              title={`Select a ${mode === 'years' ? 'year' : 'zany option'}`}
              style={{ cursor: 'pointer' }}
            />

            {/* Progress indicator */}
            {showProgress && (
              <div
                className={styles.progress}
                style={{ width: `${position * 100}%` }}
              />
            )}

            {/* Position marker */}
            {hasOptions && (
              <div
                className={styles.marker}
                style={{ left: `${showProgress ? position * 100 : 0}%` }}
              />
            )}

            {/* Tick marks for discrete steps */}
            <div className={styles.ticks}>
              {options.map((_, index) => (
                <div
                  key={index}
                  className={styles.tick}
                  style={{
                    left: `${options.length > 1 ? (index / (options.length - 1)) * 100 : 0}%`
                  }}
                />
              ))}
            </div>
          </div>

          {/* Labels */}
          <div className={styles.labels}>
            <span className={styles.labelStart}>{options[0]?.label}</span>
            <span className={styles.labelEnd}>{options[options.length - 1]?.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
